// src/Components/Discussions.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Switch,
  Popover,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Forum as ForumIcon,
  Reply as ReplyIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Chat as ChatIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  PushPin as PinIcon,
  PushPinOutlined as UnpinIcon,
  AddReaction as ReactionIcon
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, increment, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { formatDistanceToNow } from 'date-fns';
import PrivacyControl from './PrivacyControl';
import TagMembers from './TagMembers';
import EmojiPicker from './EmojiPicker';
import { notifyTaggedMembers, createNotificationForFamily, NOTIFICATION_TYPES, NOTIFICATION_PRIORITY } from '../services/notificationService';
import logger from '../utils/logger';

// Reaction types for discussions and replies
const REACTIONS = [
  { type: 'love', emoji: '❤️', label: 'Love' },
  { type: 'haha', emoji: '😂', label: 'Haha' },
  { type: 'wow', emoji: '😮', label: 'Wow' },
  { type: 'sad', emoji: '😢', label: 'Sad' },
  { type: 'angry', emoji: '😡', label: 'Angry' },
  { type: 'care', emoji: '🥰', label: 'Care' }
];

const Discussions = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
    taggedMembers: []
  });
  const [replyContent, setReplyContent] = useState('');
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  // Menu and action states
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDiscussion, setMenuDiscussion] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discussionToDelete, setDiscussionToDelete] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // Reaction states
  const [reactionAnchor, setReactionAnchor] = useState(null);
  const [reactionTarget, setReactionTarget] = useState(null); // { type: 'discussion'|'reply', id, discussionId? }
  const [replyTaggedMembers, setReplyTaggedMembers] = useState([]);

  const db = getFirestore();

  // Check if current user is admin
  const isAdmin = profile?.isAdmin || profile?.isSuperAdmin || currentUser?.isAdmin;

  useEffect(() => {
    if (!familyBoardId) return;

    const q = query(
      collection(db, 'families', familyBoardId, 'discussions'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const discussionsData = [];
        querySnapshot.forEach((doc) => {
          discussionsData.push({ id: doc.id, ...doc.data() });
        });
        setDiscussions(discussionsData);
        setLoading(false);
      },
      (error) => {
        logger.error('Error fetching discussions:', error);
        setError('Failed to load discussions');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId]);

  // Fetch replies when reply dialog opens
  useEffect(() => {
    if (!selectedDiscussion || !replyDialogOpen || !familyBoardId) {
      setReplies([]);
      return;
    }

    setLoadingReplies(true);
    const q = query(
      collection(db, 'families', familyBoardId, 'discussions', selectedDiscussion.id, 'replies'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const repliesData = [];
        querySnapshot.forEach((doc) => {
          repliesData.push({ id: doc.id, ...doc.data() });
        });
        setReplies(repliesData);
        setLoadingReplies(false);
      },
      (error) => {
        logger.error('Error fetching replies:', error);
        setLoadingReplies(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId, selectedDiscussion, replyDialogOpen]);

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required');
      return;
    }

    // Validate privacy settings
    if (formData.privacy.visibility === 'private' &&
        formData.privacy.memberIds.length === 0 &&
        formData.privacy.groupTypes.length === 0) {
      setError('Please select at least one member or group for private discussions');
      return;
    }

    try {
      const discussionData = {
        title: formData.title,
        content: formData.content,
        authorId: currentUser.uid,
        authorName: currentUser.displayName || profile?.displayName || 'Anonymous',
        replyCount: 0,
        privacy: formData.privacy,
        taggedMembers: formData.taggedMembers || [],
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'families', familyBoardId, 'discussions'), discussionData);

      const authorName = currentUser.displayName || profile?.displayName || 'Anonymous';

      // Notify all family members about the new discussion
      try {
        await createNotificationForFamily(
          familyBoardId,
          {
            type: NOTIFICATION_TYPES.DISCUSSION,
            title: 'New Discussion',
            message: `${authorName} started: "${formData.title}"`,
            priority: NOTIFICATION_PRIORITY.MEDIUM,
            actionUrl: '/discussions',
            actionLabel: 'View Discussion',
            metadata: { discussionId: docRef.id },
            createdBy: currentUser.uid,
            createdByName: authorName,
          },
          currentUser.uid
        );
      } catch (notifyErr) {
        logger.error('Discussion notification failed (non-fatal):', notifyErr);
      }

      // Send notifications to tagged members
      if (formData.taggedMembers && formData.taggedMembers.length > 0) {
        await notifyTaggedMembers(familyBoardId, formData.taggedMembers, {
          type: 'discussion',
          id: docRef.id,
          title: formData.title,
          createdBy: currentUser.uid,
          createdByName: authorName,
          actionUrl: '/discussions'
        });
      }

      setSuccess('Discussion created');
      setDialogOpen(false);
      setFormData({
        title: '',
        content: '',
        privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
        taggedMembers: []
      });
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error creating discussion:', error);
      setError('Failed to create discussion');
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      setError('Reply content is required');
      return;
    }

    try {
      const replyData = {
        content: replyContent,
        authorId: currentUser.uid,
        authorName: currentUser.displayName || profile?.displayName || 'Anonymous',
        taggedMembers: replyTaggedMembers || [],
        createdAt: serverTimestamp()
      };

      const replyRef = await addDoc(collection(db, 'families', familyBoardId, 'discussions', selectedDiscussion.id, 'replies'), replyData);

      await updateDoc(doc(db, 'families', familyBoardId, 'discussions', selectedDiscussion.id), {
        replyCount: increment(1),
        lastReplyAt: serverTimestamp()
      });

      // Send notifications to tagged members
      if (replyTaggedMembers && replyTaggedMembers.length > 0) {
        await notifyTaggedMembers(familyBoardId, replyTaggedMembers, {
          type: 'discussion_reply',
          id: replyRef.id,
          discussionId: selectedDiscussion.id,
          title: `Reply to: ${selectedDiscussion.title}`,
          createdBy: currentUser.uid,
          createdByName: currentUser.displayName || profile?.displayName || 'Anonymous',
          actionUrl: '/discussions'
        });
      }

      setSuccess('Reply posted successfully');
      setReplyContent('');
      setReplyTaggedMembers([]);
      // Don't close dialog immediately - let user see their reply appear
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error posting reply:', error);
      setError('Failed to post reply');
    }
  };

  // Handle menu open
  const handleMenuOpen = (event, discussion) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuDiscussion(discussion);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuDiscussion(null);
  };

  // Check if user can manage this discussion (delete/archive)
  const canManageDiscussion = (discussion) => {
    return discussion.authorId === currentUser?.uid || isAdmin;
  };

  // Handle archive/unarchive
  const handleArchive = async (discussion) => {
    try {
      const newArchivedState = !discussion.isArchived;
      await updateDoc(doc(db, 'families', familyBoardId, 'discussions', discussion.id), {
        isArchived: newArchivedState,
        archivedAt: newArchivedState ? serverTimestamp() : null,
        archivedBy: newArchivedState ? currentUser.uid : null
      });
      setSuccess(newArchivedState ? 'Discussion archived' : 'Discussion restored');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error archiving discussion:', error);
      setError('Failed to archive discussion');
    }
    handleMenuClose();
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = (discussion) => {
    setDiscussionToDelete(discussion);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handle actual delete
  const handleDelete = async () => {
    if (!discussionToDelete) return;

    try {
      await deleteDoc(doc(db, 'families', familyBoardId, 'discussions', discussionToDelete.id));
      setSuccess('Discussion deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error deleting discussion:', error);
      setError('Failed to delete discussion');
    }
    setDeleteDialogOpen(false);
    setDiscussionToDelete(null);
  };

  // Handle pin/unpin discussion
  const handlePin = async (discussion) => {
    try {
      const newPinnedState = !discussion.isPinned;
      await updateDoc(doc(db, 'families', familyBoardId, 'discussions', discussion.id), {
        isPinned: newPinnedState,
        pinnedAt: newPinnedState ? serverTimestamp() : null,
        pinnedBy: newPinnedState ? currentUser.uid : null
      });
      setSuccess(newPinnedState ? 'Discussion pinned to top' : 'Discussion unpinned');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error pinning discussion:', error);
      setError('Failed to pin discussion');
    }
    handleMenuClose();
  };

  // Handle reaction picker open
  const handleReactionClick = (event, targetType, targetId, discussionId = null) => {
    event.stopPropagation();
    setReactionAnchor(event.currentTarget);
    setReactionTarget({ type: targetType, id: targetId, discussionId });
  };

  // Handle reaction picker close
  const handleReactionClose = () => {
    setReactionAnchor(null);
    setReactionTarget(null);
  };

  // Handle adding/removing a reaction
  const handleReaction = async (reactionType) => {
    if (!reactionTarget) return;

    try {
      const userName = currentUser.displayName || profile?.displayName || 'Anonymous';
      let docRef;

      if (reactionTarget.type === 'discussion') {
        docRef = doc(db, 'families', familyBoardId, 'discussions', reactionTarget.id);
      } else if (reactionTarget.type === 'reply') {
        docRef = doc(db, 'families', familyBoardId, 'discussions', reactionTarget.discussionId, 'replies', reactionTarget.id);
      }

      // Get current reactions from the discussion/reply in state
      let currentReactions = {};
      if (reactionTarget.type === 'discussion') {
        const disc = discussions.find(d => d.id === reactionTarget.id);
        currentReactions = disc?.reactions || {};
      } else {
        const reply = replies.find(r => r.id === reactionTarget.id);
        currentReactions = reply?.reactions || {};
      }

      const userReaction = currentReactions[currentUser.uid];
      let newReactions = { ...currentReactions };

      if (userReaction && userReaction.type === reactionType) {
        // Remove reaction if same type clicked
        delete newReactions[currentUser.uid];
      } else {
        // Add or change reaction
        newReactions[currentUser.uid] = {
          type: reactionType,
          userName,
          timestamp: new Date().toISOString()
        };
      }

      await updateDoc(docRef, { reactions: newReactions });
    } catch (error) {
      logger.error('Error updating reaction:', error);
      setError('Failed to update reaction');
    }

    handleReactionClose();
  };

  // Get reaction counts from reactions object
  const getReactionCounts = (reactions) => {
    if (!reactions) return {};
    const counts = {};
    Object.values(reactions).forEach(r => {
      counts[r.type] = (counts[r.type] || 0) + 1;
    });
    return counts;
  };

  // Get user's current reaction
  const getUserReaction = (reactions) => {
    if (!reactions || !currentUser) return null;
    return reactions[currentUser.uid]?.type || null;
  };

  // Helper function to check if user can view a discussion based on privacy settings
  const canViewDiscussion = (discussion) => {
    // Discussion author can always see their own discussions
    if (discussion.authorId === currentUser?.uid) {
      return true;
    }

    // If no privacy settings or public, everyone can see
    if (!discussion.privacy || discussion.privacy.visibility === 'public') {
      return true;
    }

    // For private discussions, check if user is in the allowed list
    if (discussion.privacy.visibility === 'private') {
      // Check if user ID is in memberIds
      if (discussion.privacy.memberIds && discussion.privacy.memberIds.includes(currentUser?.uid)) {
        return true;
      }

      // Check if user belongs to any of the allowed groups
      if (discussion.privacy.groupTypes && discussion.privacy.groupTypes.length > 0 && profile?.groups) {
        const userGroups = profile.groups || [];
        const hasGroupAccess = discussion.privacy.groupTypes.some(groupType =>
          userGroups.includes(groupType)
        );
        if (hasGroupAccess) {
          return true;
        }
      }

      // User doesn't have access to this private discussion
      return false;
    }

    return true;
  };

  // Filter discussions based on privacy settings and archive status
  const visibleDiscussions = discussions
    .filter(discussion => {
      if (!canViewDiscussion(discussion)) return false;
      if (discussion.isArchived && !showArchived) return false;
      return true;
    })
    .sort((a, b) => {
      // Pinned discussions first
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      // Then by createdAt (most recent first)
      return 0; // Original order from query is already desc by createdAt
    });

  // Count archived discussions for the toggle label
  const archivedCount = discussions.filter(d => d.isArchived && canViewDiscussion(d)).length;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Family Discussions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start conversations and connect with your family
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {archivedCount > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  size="small"
                />
              }
              label={`Show archived (${archivedCount})`}
              sx={{ mr: 0 }}
            />
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            New Discussion
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {visibleDiscussions.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
          <ForumIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No discussions yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Start a conversation with your family!
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Start First Discussion
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {visibleDiscussions.map((discussion) => (
            <Grid item xs={12} key={discussion.id}>
              <Card sx={{
                '&:hover': { boxShadow: 4 },
                transition: 'all 0.3s',
                opacity: discussion.isArchived ? 0.7 : 1,
                borderLeft: discussion.isPinned ? '4px solid' : discussion.isArchived ? '4px solid' : 'none',
                borderColor: discussion.isPinned ? 'primary.main' : 'warning.main'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar sx={{ bgcolor: discussion.isPinned ? 'primary.main' : 'secondary.main', width: 48, height: 48 }}>
                      {discussion.isPinned ? <PinIcon /> : <PersonIcon />}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, flex: 1 }}>
                          {discussion.title}
                          {discussion.isPinned && (
                            <Chip
                              icon={<PinIcon sx={{ fontSize: 14 }} />}
                              label="Pinned"
                              size="small"
                              color="primary"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {discussion.isArchived && (
                            <Chip
                              label="Archived"
                              size="small"
                              color="warning"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {discussion.content}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {discussion.authorName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {discussion.createdAt && formatDistanceToNow(discussion.createdAt.toDate(), { addSuffix: true })}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          icon={<ChatIcon />}
                          label={`${discussion.replyCount || 0} replies`}
                          onClick={() => {
                            setSelectedDiscussion(discussion);
                            setReplyDialogOpen(true);
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                        {/* Reaction button */}
                        <Tooltip title="Add reaction">
                          <IconButton
                            size="small"
                            onClick={(e) => handleReactionClick(e, 'discussion', discussion.id)}
                            sx={{ color: getUserReaction(discussion.reactions) ? 'primary.main' : 'text.secondary' }}
                          >
                            <ReactionIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Display reaction counts */}
                        {Object.entries(getReactionCounts(discussion.reactions)).map(([type, count]) => {
                          const reaction = REACTIONS.find(r => r.type === type);
                          return reaction ? (
                            <Tooltip key={type} title={`${count} ${reaction.label}`}>
                              <Chip
                                size="small"
                                label={`${reaction.emoji} ${count}`}
                                sx={{ height: 22, fontSize: '0.75rem', cursor: 'pointer' }}
                                onClick={(e) => handleReactionClick(e, 'discussion', discussion.id)}
                              />
                            </Tooltip>
                          ) : null;
                        })}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                      {canManageDiscussion(discussion) && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, discussion)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                      <Button
                        size="small"
                        startIcon={<ReplyIcon />}
                        onClick={() => {
                          setSelectedDiscussion(discussion);
                          setReplyDialogOpen(true);
                        }}
                      >
                        Reply
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => menuDiscussion && handlePin(menuDiscussion)}>
          <ListItemIcon>
            {menuDiscussion?.isPinned ? <UnpinIcon fontSize="small" /> : <PinIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{menuDiscussion?.isPinned ? 'Unpin' : 'Pin to Top'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuDiscussion && handleArchive(menuDiscussion)}>
          <ListItemIcon>
            {menuDiscussion?.isArchived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{menuDiscussion?.isArchived ? 'Restore' : 'Archive'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuDiscussion && handleDeleteClick(menuDiscussion)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Discussion Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start New Discussion</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              multiline
              rows={4}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, content: formData.content + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />

            <Divider sx={{ my: 2 }}>
              <Chip label="Privacy & Tagging" size="small" />
            </Divider>

            {/* Privacy Control */}
            <PrivacyControl
              familyCode={familyBoardId}
              value={formData.privacy}
              onChange={(privacy) => setFormData({ ...formData, privacy })}
              label="Who can see this discussion?"
            />

            {/* Tag Members */}
            <TagMembers
              familyCode={familyBoardId}
              value={formData.taggedMembers}
              onChange={(taggedMembers) => setFormData({ ...formData, taggedMembers })}
              label="Tag Family Members"
              helperText="Tagged members will receive a notification about this discussion"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Discussion Replies</DialogTitle>
        <DialogContent>
          {selectedDiscussion && (
            <>
              {/* Original Post */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, borderLeft: 4, borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Original Post: {selectedDiscussion.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedDiscussion.content}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Posted by {selectedDiscussion.authorName} • {selectedDiscussion.createdAt && formatDistanceToNow(selectedDiscussion.createdAt.toDate(), { addSuffix: true })}
                </Typography>
              </Box>

              {/* Existing Replies */}
              <Box sx={{ mb: 3, maxHeight: '300px', overflowY: 'auto' }}>
                {loadingReplies ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : replies.length > 0 ? (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                      {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
                    </Typography>
                    {replies.map((reply) => (
                      <Paper key={reply.id} sx={{ p: 2, mb: 1.5, bgcolor: 'background.default' }}>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {reply.authorName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {reply.content}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {reply.createdAt && formatDistanceToNow(reply.createdAt.toDate(), { addSuffix: true })}
                              </Typography>
                              {/* Reaction button for reply */}
                              <Tooltip title="Add reaction">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleReactionClick(e, 'reply', reply.id, selectedDiscussion.id)}
                                  sx={{
                                    p: 0.25,
                                    color: getUserReaction(reply.reactions) ? 'primary.main' : 'text.secondary'
                                  }}
                                >
                                  <ReactionIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              {/* Display reply reaction counts */}
                              {Object.entries(getReactionCounts(reply.reactions)).map(([type, count]) => {
                                const reaction = REACTIONS.find(r => r.type === type);
                                return reaction ? (
                                  <Chip
                                    key={type}
                                    size="small"
                                    label={`${reaction.emoji} ${count}`}
                                    sx={{ height: 18, fontSize: '0.7rem', cursor: 'pointer' }}
                                    onClick={(e) => handleReactionClick(e, 'reply', reply.id, selectedDiscussion.id)}
                                  />
                                ) : null;
                              })}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <ChatIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No replies yet. Be the first to reply!
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* New Reply Input */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Add Your Reply
              </Typography>
              <TextField
                fullWidth
                label="Your Reply"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                multiline
                rows={3}
                required
                placeholder="Share your thoughts... Use @name to mention someone"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <EmojiPicker
                        onEmojiSelect={(emoji) => setReplyContent(prev => prev + emoji)}
                        size="small"
                      />
                    </InputAdornment>
                  )
                }}
              />

              {/* Tag Members in Reply */}
              <Box sx={{ mt: 2 }}>
                <TagMembers
                  familyCode={familyBoardId}
                  value={replyTaggedMembers}
                  onChange={(members) => setReplyTaggedMembers(members)}
                  label="Mention Members (optional)"
                  helperText="Tagged members will be notified of your reply"
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setReplyDialogOpen(false);
            setReplyTaggedMembers([]);
          }}>Cancel</Button>
          <Button onClick={handleReply} variant="contained" color="primary" disabled={!replyContent.trim()}>
            Post Reply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Discussion?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this discussion?
          </Typography>
          {discussionToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                "{discussionToDelete.title}"
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {discussionToDelete.replyCount || 0} replies will also be deleted
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. Consider archiving instead if you want to preserve the discussion.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reaction Picker Popover */}
      <Popover
        open={Boolean(reactionAnchor)}
        anchorEl={reactionAnchor}
        onClose={handleReactionClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ display: 'flex', gap: 0.5, p: 1 }}>
          {REACTIONS.map((reaction) => (
            <Tooltip key={reaction.type} title={reaction.label}>
              <IconButton
                size="small"
                onClick={() => handleReaction(reaction.type)}
                sx={{
                  fontSize: '1.5rem',
                  '&:hover': { transform: 'scale(1.2)' },
                  transition: 'transform 0.2s'
                }}
              >
                {reaction.emoji}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Popover>
    </Container>
  );
};

export default Discussions;
